import { useState, useMemo } from "react";
import { cn } from "../../lib/utils";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowLeft02Icon } from "@hugeicons/core-free-icons";
import {
  LAUNCH_DIRECTORIES,
  DIRECTORY_CATEGORIES,
  type DirectoryEntry,
} from "../../data/directories-channels";

interface DirectoriesChannelPageProps {
  onBack: () => void;
}

const PRIORITY_COLORS: Record<DirectoryEntry["priority"], { bg: string; text: string; ring: string }> = {
  high: {
    bg: "bg-[color-mix(in_srgb,var(--color-state-success)_12%,transparent)]",
    text: "text-[var(--color-state-success)]",
    ring: "ring-[color-mix(in_srgb,var(--color-state-success)_22%,transparent)]",
  },
  medium: {
    bg: "bg-[color-mix(in_srgb,var(--color-state-warning)_12%,transparent)]",
    text: "text-[var(--color-state-warning)]",
    ring: "ring-[color-mix(in_srgb,var(--color-state-warning)_22%,transparent)]",
  },
  low: {
    bg: "bg-[var(--color-bg-hover)]",
    text: "text-[var(--color-text-muted)]",
    ring: "ring-[var(--color-border-stroke)]",
  },
};

export function DirectoriesChannelPage({ onBack }: DirectoriesChannelPageProps) {
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<DirectoryEntry["category"] | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<DirectoryEntry["priority"] | "all">("all");

  const filtered = useMemo(() => {
    return LAUNCH_DIRECTORIES.filter((d) => {
      if (categoryFilter !== "all" && d.category !== categoryFilter) return false;
      if (priorityFilter !== "all" && d.priority !== priorityFilter) return false;
      if (search && !d.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [search, categoryFilter, priorityFilter]);

  const grouped = useMemo(() => {
    const map = new Map<DirectoryEntry["category"], DirectoryEntry[]>();
    for (const d of filtered) {
      const existing = map.get(d.category) ?? [];
      existing.push(d);
      map.set(d.category, existing);
    }
    return map;
  }, [filtered]);

  const categoryOrder: DirectoryEntry["category"][] = ["ai", "startup", "saas", "general", "community", "deals"];

  const stats = useMemo(() => ({
    total: LAUNCH_DIRECTORIES.length,
    high: LAUNCH_DIRECTORIES.filter((d) => d.priority === "high").length,
    medium: LAUNCH_DIRECTORIES.filter((d) => d.priority === "medium").length,
    low: LAUNCH_DIRECTORIES.filter((d) => d.priority === "low").length,
  }), []);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-3)]">
        <button
          onClick={onBack}
          className={cn(
            "h-[32px] w-[32px] rounded-[var(--radius-md)] flex items-center justify-center",
            "text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)]",
            "transition-colors duration-[var(--duration-fast)] cursor-pointer",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-focus)]",
          )}
          aria-label="Back to channels"
        >
          <HugeiconsIcon icon={ArrowLeft02Icon} size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="text-[14px] font-semibold text-[var(--color-text-primary)]">Launch Directories</h2>
          <span className="text-[14px] text-[var(--color-text-muted)]">
            {stats.total} directories · {stats.high} high priority · {stats.medium} medium · {stats.low} low
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="shrink-0 px-[var(--spacing-4)] py-[var(--spacing-3)] border-b border-[var(--color-border-stroke)] flex items-center gap-[var(--spacing-2)] flex-wrap">
        <input
          type="text"
          placeholder="Search directories…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={cn(
            "h-[32px] w-[220px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)]",
            "text-[14px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)]",
            "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
            "hover:border-[var(--color-bg-hover)] transition-colors",
          )}
        />
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as DirectoryEntry["category"] | "all")}
          className={cn(
            "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
            "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
            "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
          )}
        >
          <option value="all">All Categories</option>
          {categoryOrder.map((cat) => (
            <option key={cat} value={cat}>{DIRECTORY_CATEGORIES[cat]}</option>
          ))}
        </select>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value as DirectoryEntry["priority"] | "all")}
          className={cn(
            "h-[32px] rounded-[var(--radius-md)] border border-[var(--color-border-stroke)]",
            "bg-[var(--color-bg-canvas)] px-[var(--spacing-2)] pr-[var(--spacing-6)]",
            "text-[14px] text-[var(--color-text-primary)] appearance-none cursor-pointer",
            "focus-visible:outline-none focus-visible:border-[var(--color-accent-focus)]",
          )}
        >
          <option value="all">All Priorities</option>
          <option value="high">High Priority</option>
          <option value="medium">Medium Priority</option>
          <option value="low">Low Priority</option>
        </select>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-[var(--spacing-4)] py-[var(--spacing-4)] space-y-[var(--spacing-6)]">
          {categoryOrder.map((cat) => {
            const items = grouped.get(cat);
            if (!items || items.length === 0) return null;
            return (
              <div key={cat}>
                <div className="flex items-center gap-[var(--spacing-2)] mb-[var(--spacing-3)]">
                  <h3 className="text-[14px] font-semibold text-[var(--color-text-muted)] uppercase tracking-[0.04em]">
                    {DIRECTORY_CATEGORIES[cat]}
                  </h3>
                  <span className="text-[14px] tabular-nums text-[var(--color-text-muted)]">{items.length}</span>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-[var(--spacing-2)]">
                  {items.map((dir, idx) => (
                    <DirectoryCard key={dir.name} entry={dir} index={idx} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DirectoryCard({ entry, index }: { entry: DirectoryEntry; index: number }) {
  const colors = PRIORITY_COLORS[entry.priority];

  return (
    <div
      className={cn(
        "rounded-[var(--radius-lg)] border border-[var(--color-border-stroke)] bg-[var(--color-bg-surface)]",
        "px-[var(--spacing-3)] py-[var(--spacing-3)]",
        "hover:bg-[var(--color-bg-hover)] transition-colors duration-[var(--duration-fast)]",
        "animate-stagger-in",
      )}
      style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
    >
      <div className="flex items-center justify-between gap-[var(--spacing-2)]">
        <span className="text-[14px] font-medium text-[var(--color-text-primary)] truncate">{entry.name}</span>
        <span className={cn(
          "inline-flex items-center shrink-0 rounded-[var(--radius-sm)] px-[6px] py-[1px] text-[14px] font-medium ring-1 ring-inset capitalize",
          colors.bg, colors.text, colors.ring,
        )}>
          {entry.priority}
        </span>
      </div>
    </div>
  );
}
